import React from "react";
import {Grid} from "@mui/material";
import {EmployeeRef, INeighbourhood, OfficeVisitSheetRow} from "../../api/Model";
import {SetState} from "../../util/FormHandlers";
import {isWeekend} from "../../util/DateUtil";
import {filterSerializer} from "../../util/FilterSerializer";
import {TextSearchFilter} from "../../components/filter-fields/TextSearchFilter";
import {ClearFilterButton} from "../../components/filter-fields/ClearFilterButton";
import {NeighbourhoodsFilter} from "../../components/filter-fields/NeighbourhoodsFilter";
import {DateRangeFilter, DateRangeOption, DateRangeOptions} from "../../components/filter-fields/DateRangeFilter";
import {EmployeeNeighbourhood} from "../../api/EmployeeNeighbourhoodStore";
import {numberCmp} from "../../util/SortUtil";
import {ResidenceFilter} from "../../components/filter-fields/ResidenceFilter";
import {EmployeeMap} from "../../api/EmployeeMapStore";
import {matchResidence, Residence, ResidenceModel} from "../../api/ResidenceStore";
import {filterOfficeVisitReport} from "../../util/visit/VisitReportSheetUtil";
import {andFilter, filterDateRange, filterNeighbourhoods, filterText, filterValue} from "../../util/FilterUtil";
import {EmployeeVisitSheet, EmployeeVisitType} from "../../api/EmployeeVisitStore";

export interface EmployeeVisitReportFilterForm {
    dateRange: DateRangeOption;
    search: string;
    neighbourhoods: INeighbourhood[];
    residency: Residence | null;
}

export const emptyEmployeeVisitReportFilter: EmployeeVisitReportFilterForm = {
    search: '',
    dateRange: DateRangeOptions.lastThreeMonths,
    neighbourhoods: [],
    residency: null,
}

export const employeeVisitReportFilterSerializer = (
    allNeighbourhoods: INeighbourhood[],
    residenceModel: ResidenceModel,
) => filterSerializer<EmployeeVisitReportFilterForm>(
    {
        ...emptyEmployeeVisitReportFilter,
        residency: residenceModel.residents,
    },
    b => {
        b.dateRange('dateRange');
        b.str('search');
        b.neighbourhoods('neighbourhoods', allNeighbourhoods);
        b.residency('residency', residenceModel);
    },
);

export function filterEmployeeVisitReportByDates(sheet: EmployeeVisitSheet, filterDates: Set<string>): EmployeeVisitSheet {
    return filterOfficeVisitReport(sheet, date => filterDates.has(date));
}

export type EmployeeVisitFigure = {
    workDayVisits: number,
    tenureDayCount: number,
    percent: number
};

export function workDayVisitCounter<Row>(dates: string[]): (row: OfficeVisitSheetRow<Row, EmployeeVisitType>) => EmployeeVisitFigure {
    const workDayIndices = dates
        .map((date, dateIndex) => ({date, dateIndex}))
        .filter(({date}) => !isWeekend(date))
        .map(({dateIndex}) => dateIndex);


    return row => {
        const tenureDayCount = workDayIndices
            .filter(dateIndex => row.visits[dateIndex] !== EmployeeVisitType.BeforeStart)
            .length;
        const workDayVisits = row.visits
            .filter(visit => visit === EmployeeVisitType.WorkDayVisit)
            .length;
        return {
            workDayVisits,
            tenureDayCount,
            percent: tenureDayCount === 0 ? Number.NaN : Math.round(workDayVisits * 100 / tenureDayCount),
        };
    };
}

export function filterEmployeeVisitReport(
    sheet: EmployeeVisitSheet,
    employee2neighbourhood: EmployeeNeighbourhood,
    employeeMap: EmployeeMap,
    filter: EmployeeVisitReportFilterForm,
): EmployeeVisitSheet {
    const filteredSheet = filterOfficeVisitReport(
        sheet,
        filterDateRange(filter.dateRange),
        andFilter(
            filterValue(
                matchResidence(filter.residency),
                (employee: EmployeeRef) => employeeMap.get(employee),
            ),
            filterNeighbourhoods(filter.neighbourhoods, employee => employee2neighbourhood.get(employee)),
            filterText(filter.search, employee => employee.fullName),
        ),
    );

    const countPercent = workDayVisitCounter(filteredSheet.dates);
    const sortedRows = filteredSheet.rows
        .map(row => ({
            ...row,
            visitPercent: countPercent(row).percent ?? 0,
        }))
        .sort(numberCmp('visitPercent', true));

    return {
        ...filteredSheet,
        rows: sortedRows,
    };
}

export function EmployeeVisitReportFilter({filter, setFilter}: {
    filter: EmployeeVisitReportFilterForm,
    setFilter: SetState<EmployeeVisitReportFilterForm>,
}) {
    return (
        <Grid container spacing={2} alignItems="center">
            <DateRangeFilter filter={filter} setFilter={setFilter} property="dateRange"/>
            <ResidenceFilter filter={filter} setFilter={setFilter}/>
            <NeighbourhoodsFilter filter={filter} setFilter={setFilter}/>
            <TextSearchFilter filter={filter} setFilter={setFilter} property="search"/>
            <ClearFilterButton emptyFilter={emptyEmployeeVisitReportFilter} setFilter={setFilter}/>
        </Grid>
    );
}
